import React, { useState } from "react"
import { Button, Row, Col, Spin, Flex, Card, Typography, Image } from "antd"
import { useTranslation } from "react-i18next"
import PropTypes from "prop-types"
import { ExperienceCard } from "ui/ExperienceCard"
import styles from "./ExperiencesHome.module.css"
import { EXPERIENCE_STATES } from "constants/experiences"
import { useExperiencesActions } from "../../../../hooks/useExperiencesActions"
import { getEmptyStateTranslations } from "utils/translations"
import CustomPagination from "components/common/CustomPagination"
import useActiveGuide from "hooks/useActiveClient"
import { BookingNotFound } from "ui/Icon/DSIcon/BookingNotFound"
import PrivateListingIcon from "assets/icons/new/private-listing.svg"
import TicketListingIcon from "assets/icons/new/ticket-listing.svg"
import RentalListingIcon from "assets/icons/new/rental-listing.svg"

const { Text, Title, Paragraph } = Typography

export const EmptyState = ({ onAddNewExperience, type }) => {
  const { t } = useTranslation()
  const translationKey = getEmptyStateTranslations(type)
  const { isActive: active, isPending } = useActiveGuide()
  const [loadingType, setLoadingType] = useState(null)

  const handleAddClick = async (typeKey) => {
    setLoadingType(typeKey)
    try {
      await Promise.resolve(onAddNewExperience?.(typeKey))
    } finally {
      setLoadingType(null)
    }
  }

  const listingTypes = [
    {
      key: "private",
      title: t("experiences.private_listing"),
      description: t("experiences.private_listing_description"),
      iconSrc: PrivateListingIcon,
      buttonLabel: t("experiences.empty_state.add_new_private")
    },
    {
      key: "ticketed",
      title: t("experiences.ticketed_listing"),
      description: t("experiences.ticketed_listing_description"),
      iconSrc: TicketListingIcon,
      buttonLabel: t("experiences.empty_state.add_new_ticketed")
    },
    {
      key: "rental",
      title: t("experiences.rental_listing"),
      description: t("experiences.rental_listing_description"),
      iconSrc: RentalListingIcon,
      buttonLabel: t("experiences.empty_state.add_new_rental")
    }
  ]

  return (
    <Flex vertical align="center" className={styles.emptyState}>
      <Flex align="center" justify="center" className={styles.imgWrapper}>
        <BookingNotFound />
      </Flex>
      <Text className={styles.emptyStateInfo}>{t(translationKey.info)}</Text>
      {type === EXPERIENCE_STATES.published && (
        <Row gutter={[16, 16]} className={styles.emptyStateCards}>
          {listingTypes.map((listing) => (
            <Col xs={24} md={8} key={listing.key}>
              <Card className={styles.emptyStateCard}>
                <Flex vertical align="center" justify="space-between" className={styles.emptyStateCardContent}>
                  <Flex vertical align="center">
                    <Flex align="center" justify="center" className={styles.emptyStateCardIcon}>
                      <Image src={listing.iconSrc} alt={listing.title} preview={false} className={styles.emptyStateCardImage} />
                    </Flex>
                    <Title level={5} className={styles.emptyStateCardTitle}>
                      {listing.title}
                    </Title>
                    <Paragraph className={styles.emptyStateCardDescription}>{listing.description}</Paragraph>
                  </Flex>
                  <Button
                    size="large"
                    color="primary"
                    variant="solid"
                    className={styles.emptyStateCardButton}
                    onClick={() => handleAddClick(listing.key)}
                    disabled={!active || isPending || Boolean(loadingType)}
                    loading={loadingType === listing.key}
                  >
                    {listing.buttonLabel}
                  </Button>
                </Flex>
              </Card>
            </Col>
          ))}
        </Row>
      )}
      {type !== EXPERIENCE_STATES.published && <Paragraph className={styles.emptyStateSubtitle}>{t(translationKey.subtitle)}</Paragraph>}
    </Flex>
  )
}

export const TabContent = ({
  trips,
  onAddNewExperience,
  type,
  onActionComplete,
  metadata,
  pageSize,
  setPage,
  setPageSize,
  loading,
  showPagination,
  showSuggestions
}) => {
  const { handleDelete, handleDuplicate, handleDeactivate, handleShare, handlePublish, modal } = useExperiencesActions()
  const uiType = type === "unpublished" ? "deactivated" : type

  if (loading && !showSuggestions) {
    return (
      <div className={styles.centeredLoader}>
        <Spin size="large" />
      </div>
    )
  }

  if (trips.length === 0) {
    return <EmptyState onAddNewExperience={onAddNewExperience} type={uiType} />
  }
  const renderGrid = () => (
    <Row gutter={[20, 20]}>
      {trips.map((trip) => (
        <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={12} key={trip.id}>
          <ExperienceCard
            trip={trip}
            uiType={uiType}
            onDelete={() => handleDelete(trip, onActionComplete)}
            onDeactivate={() => handleDeactivate(trip, onActionComplete)}
            onDuplicate={() => handleDuplicate(trip, onActionComplete)}
            onShare={() => handleShare(trip)}
            onPublish={() => handlePublish(trip, onActionComplete)}
          />
        </Col>
      ))}
    </Row>
  )

  const allPageSizeOptions = [10, 20, 30, 50, 100, 200, 500, 1000]
  let pageSizeOptions = allPageSizeOptions.filter((opt) => opt < metadata.total_results)
  const nextOption = allPageSizeOptions.find((opt) => opt >= metadata.total_results)
  if (nextOption) pageSizeOptions.push(nextOption)
  return (
    <>
      {renderGrid()}
      {showPagination && metadata?.total_results > 10 && (
        <div className={styles.paginationContainer}>
          {
            <CustomPagination
              metadata={metadata}
              pageSize={pageSize}
              total={metadata.total_results}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              pageSizeOptions={pageSizeOptions}
            />
          }
        </div>
      )}
      {modal}
    </>
  )
}

EmptyState.propTypes = {
  onAddNewExperience: PropTypes.func.isRequired,
  type: PropTypes.oneOf(["draft", "published", "deactivated", "unpublished"]).isRequired
}

TabContent.propTypes = {
  trips: PropTypes.arrayOf(PropTypes.object).isRequired,
  onAddNewExperience: PropTypes.func.isRequired,
  type: PropTypes.oneOf(["draft", "published", "deactivated", "unpublished"]).isRequired,
  onActionComplete: PropTypes.func.isRequired,
  metadata: PropTypes.shape({
    total_results: PropTypes.number
  }),
  pageSize: PropTypes.number.isRequired,
  setPage: PropTypes.func.isRequired,
  setPageSize: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  showSuggestions: PropTypes.bool.isRequired,
  showPagination: PropTypes.bool.isRequired
}
